import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSitemap,
  faPlus,
  faCheck,
  faXmark,
  faSort,
  faSortUp,
  faSortDown,
  faToggleOn,
  faToggleOff,
  faDownload,
  faSearch,
  faInfoCircle,
  faCopy,
  faBuilding,
  faAddressBook,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../ui/ActionButton";
import FormattedDate from "../ui/FormattedDate";
import PhoneInput from "../ui/PhoneInput";
import LastUpdatedBadge from "../ui/LastUpdatedBadge";
import LoadingSpinner from "../ui/LoadingSpinner";
import { useDataCache, CACHE_KEYS } from "../../contexts/DataCacheContext";
import {
  fetchOrganizations,
  createOrganization,
  updateOrganization,
  bulkUpdateOrganizations,
} from "../../api/adminApi";

// check if form has changes
const hasFormChanges = (original, current) => {
  if (!original || !current) return false;
  return JSON.stringify(original) !== JSON.stringify(current);
};

function OrganizationsSection({ darkMode, t }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [originalEditingOrg, setOriginalEditingOrg] = useState(null);
  const [newOrg, setNewOrg] = useState({
    name: "",
    max_accounts: 10,
    primary_contact_email: "",
    primary_contact_phone: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [bulkTogglingIds, setBulkTogglingIds] = useState([]);
  const [bulkTogglingAction, setBulkTogglingAction] = useState(null); // "activate" or "deactivate"
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // caching state
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const { getCachedData, setCachedData } = useDataCache();
  const isFetching = useRef(false);

  const detailPanelRef = useRef(null);
  const sectionTopRef = useRef(null);

  const scrollToTop = () => {
    setTimeout(() => {
      sectionTopRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };
  const [toastFading, setToastFading] = useState(false);

  useEffect(() => {
    if (snackbar.open) {
      const fadeTimer = setTimeout(() => {
        setToastFading(true);
      }, 2500);

      const closeTimer = setTimeout(() => {
        setSnackbar((s) => ({ ...s, open: false }));
        setToastFading(false);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(closeTimer);
      };
    }
  }, [snackbar.open]);

  // load organizations with caching
  const loadData = useCallback(
    async (forceRefresh = false) => {
      if (isFetching.current) return;

      // check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedOrgs = getCachedData(CACHE_KEYS.ADMIN_ORGANIZATIONS);

        if (cachedOrgs?.data) {
          console.log(
            "Using cached organizations data (age: " +
              Math.round((Date.now() - cachedOrgs.timestamp) / 1000 / 60) +
              " min)",
          );
          setOrganizations(cachedOrgs.data.organizations || []);
          setLastUpdated(cachedOrgs.timestamp);
          setLoading(false);
          return;
        }
      }

      isFetching.current = true;

      try {
        if (forceRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        console.log("Fetching fresh organizations from API...");

        const orgsData = await fetchOrganizations();
        const timestamp = Date.now();

        setOrganizations(orgsData.organizations || []);
        setLastUpdated(timestamp);

        setCachedData(CACHE_KEYS.ADMIN_ORGANIZATIONS, orgsData, timestamp);

        console.log("Fresh organizations fetched and cached");
      } catch (err) {
        console.error("Failed to load organizations:", err);
        setError(t("Failed to load organizations"));
      } finally {
        setLoading(false);
        setRefreshing(false);
        isFetching.current = false;
      }
    },
    [t, getCachedData, setCachedData],
  );

  // handle refresh button click
  const handleRefresh = useCallback(() => {
    loadData(true);
  }, [loadData]);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // scroll to detail panel when it opens
  const scrollToDetailPanel = () => {
    setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const handleCopyEmail = (e, uniqueId, email) => {
    e.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmailId(uniqueId);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  const filteredOrgs = useMemo(() => {
    let result = organizations.filter((org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    if (filterStatus !== "all") {
      result = result.filter((org) =>
        filterStatus === "active" ? org.is_active : !org.is_active,
      );
    }

    result.sort((a, b) => {
      let aVal = a[sortConfig.key] ?? "";
      let bVal = b[sortConfig.key] ?? "";

      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [organizations, searchQuery, sortConfig, filterStatus]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredOrgs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredOrgs.map((o) => o.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleCreate = async () => {
    try {
      const result = await createOrganization({
        name: newOrg.name,
        max_accounts: newOrg.max_accounts,
        primary_contact_email: newOrg.primary_contact_email,
        primary_contact_phone: newOrg.primary_contact_phone,
      });

      // add new org to state
      const org = {
        id: result.id,
        name: newOrg.name,
        max_accounts: newOrg.max_accounts,
        used_accounts: 0,
        active_accounts: 0,
        admin_count: 0,
        primary_contact_email: newOrg.primary_contact_email,
        primary_contact_phone: newOrg.primary_contact_phone,
        total_ghg_kg: 0,
        is_active: true,
        created_at: new Date().toISOString().split("T")[0],
      };
      setOrganizations([...organizations, org]);
      setNewOrg({
        name: "",
        max_accounts: 10,
        primary_contact_email: "",
        primary_contact_phone: "",
      });
      setShowCreatePanel(false);

      // invalidate cache
      setCachedData(
        CACHE_KEYS.ADMIN_ORGANIZATIONS,
        { organizations: [...organizations, org] },
        Date.now(),
      );

      setSnackbar({
        open: true,
        message: "Organization created successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to create organization:", err);
      setError(t("Failed to create organization"));
    }
  };

  const handleEdit = (org) => {
    setEditingOrg({ ...org });
    setOriginalEditingOrg({ ...org });
    setShowCreatePanel(false); // close create panel if open
    scrollToDetailPanel();
  };

  const handleSaveEdit = async () => {
    try {
      await updateOrganization(editingOrg.id, {
        name: editingOrg.name,
        max_accounts: editingOrg.max_accounts,
        primary_contact_email: editingOrg.primary_contact_email,
        primary_contact_phone: editingOrg.primary_contact_phone,
        is_active: editingOrg.is_active,
      });

      const updatedOrgs = organizations.map((org) =>
        org.id === editingOrg.id ? editingOrg : org,
      );
      setOrganizations(updatedOrgs);
      setEditingOrg(null);
      setOriginalEditingOrg(null);

      setCachedData(
        CACHE_KEYS.ADMIN_ORGANIZATIONS,
        { organizations: updatedOrgs },
        Date.now(),
      );

      setSnackbar({
        open: true,
        message: "Organization updated successfully",
        severity: "success",
      });

      scrollToTop();
    } catch (err) {
      console.error("Failed to update organization:", err);
      setError(t("Failed to update organization"));
    }
  };

  const toggleActive = async (orgId) => {
    if (togglingId || bulkTogglingIds.length > 0) return;

    const org = organizations.find((o) => o.id === orgId);
    if (!org) return;

    setTogglingId(orgId);
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600;
    const newStatus = !org.is_active;

    try {
      await updateOrganization(orgId, { is_active: newStatus });

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      const timestamp = Date.now();
      const updatedOrgs = organizations.map((o) =>
        o.id === orgId ? { ...o, is_active: newStatus } : o,
      );
      setOrganizations(updatedOrgs);
      setLastUpdated(timestamp);

      setCachedData(
        CACHE_KEYS.ADMIN_ORGANIZATIONS,
        { organizations: updatedOrgs },
        timestamp,
      );

      setSnackbar({
        open: true,
        message: newStatus
          ? "Organization activated"
          : "Organization deactivated",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to toggle organization status:", err);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      setError(t("Failed to update organization"));
    } finally {
      setTogglingId(null);
    }
  };

  const bulkSetActive = async (active) => {
    if (bulkTogglingIds.length > 0 || togglingId) return;

    const count = selectedIds.length;
    setBulkTogglingIds([...selectedIds]);
    setBulkTogglingAction(active ? "activate" : "deactivate");
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600;

    try {
      await bulkUpdateOrganizations(selectedIds, active);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      const timestamp = Date.now();
      const updatedOrgs = organizations.map((org) =>
        selectedIds.includes(org.id) ? { ...org, is_active: active } : org,
      );
      setOrganizations(updatedOrgs);
      setSelectedIds([]);
      setLastUpdated(timestamp);

      setCachedData(
        CACHE_KEYS.ADMIN_ORGANIZATIONS,
        { organizations: updatedOrgs },
        timestamp,
      );

      setSnackbar({
        open: true,
        message: active
          ? `${count} organization${count > 1 ? "s" : ""} activated`
          : `${count} organization${count > 1 ? "s" : ""} deactivated`,
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to bulk update organizations:", err);

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_DISPLAY_TIME - elapsed),
        );
      }

      setError(t("Failed to update organizations"));
    } finally {
      setBulkTogglingIds([]);
      setBulkTogglingAction(null);
    }
  };

  const exportCSV = () => {
    const headers = [
      "Name",
      "Primary Email",
      "Phone",
      "Admins",
      "Active Accounts",
      "Total Accounts",
      "GHG Diverted (kg)",
      "Status",
      "Created",
    ];
    const rows = filteredOrgs.map((org) => [
      org.name,
      org.primary_contact_email,
      org.primary_contact_phone,
      org.admin_count,
      org.active_accounts,
      org.used_accounts,
      org.total_ghg_kg,
      org.is_active ? "Active" : "Inactive",
      org.created_at,
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "organizations.csv";
    a.click();
  };

  const allSelected =
    filteredOrgs.length > 0 && selectedIds.length === filteredOrgs.length;
  const someSelected = selectedIds.length > 0;

  if (loading && organizations.length === 0) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <LoadingSpinner size={40} />
          <span>{t("Loading organizations...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section admin-section-wide" ref={sectionTopRef}>
      <div className="profile-section-header">
        <div className="profile-section-icon admin-icon">
          <FontAwesomeIcon icon={faSitemap} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 className="profile-section-title">{t("Organizations")}</h2>
          <p className="profile-section-desc">
            {t("Manage client organizations and their account limits")}
          </p>
        </div>
        <div className="header-status-area">
          {snackbar.open && (
            <div
              className={`inline-toast ${snackbar.severity} ${toastFading ? "fading" : ""}`}
            >
              <FontAwesomeIcon icon={faCheck} />
              <span>{t(snackbar.message)}</span>
            </div>
          )}
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={handleRefresh}
            isRefreshing={refreshing}
            variant="admin"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t("Search organizations...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="admin-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t("All Status")}</option>
            <option value="active">{t("Active")}</option>
            <option value="inactive">{t("Inactive")}</option>
          </select>
        </div>
        <div className="admin-toolbar-right">
          <button
            className="admin-toolbar-btn"
            onClick={exportCSV}
            title={t("Export CSV")}
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            className="admin-toolbar-btn admin-primary"
            onClick={() => {
              setShowCreatePanel(true);
              setEditingOrg(null);
              setOriginalEditingOrg(null);
              scrollToDetailPanel();
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("New Organization")}</span>
          </button>
        </div>
      </div>

      {/* bulk action bar */}
      {someSelected && (
        <div className="admin-bulk-bar">
          <span className="admin-bulk-count">
            {selectedIds.length} {t("selected")}
          </span>
          <div className="admin-bulk-actions">
            <button
              className="admin-bulk-btn success"
              onClick={() => bulkSetActive(true)}
              disabled={bulkTogglingIds.length > 0 || !!togglingId}
            >
              <FontAwesomeIcon
                icon={
                  bulkTogglingAction === "activate" ? faSpinner : faToggleOn
                }
                spin={bulkTogglingAction === "activate"}
              />
              {bulkTogglingAction === "activate"
                ? t("Activating...")
                : t("Activate")}
            </button>
            <button
              className="admin-bulk-btn warning"
              onClick={() => bulkSetActive(false)}
              disabled={bulkTogglingIds.length > 0 || !!togglingId}
            >
              <FontAwesomeIcon
                icon={
                  bulkTogglingAction === "deactivate" ? faSpinner : faToggleOff
                }
                spin={bulkTogglingAction === "deactivate"}
              />
              {bulkTogglingAction === "deactivate"
                ? t("Deactivating...")
                : t("Deactivate")}
            </button>
            <button
              className="admin-bulk-btn"
              onClick={() => setSelectedIds([])}
              disabled={bulkTogglingIds.length > 0}
            >
              <FontAwesomeIcon icon={faXmark} />
              {t("Clear")}
            </button>
          </div>
        </div>
      )}

      {/* table */}
      <div
        className={`admin-table-container ${refreshing ? "shimmer-overlay" : ""}`}
      >
        <table className="admin-table">
          <thead>
            <tr>
              <th className="th-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="sortable" onClick={() => handleSort("name")}>
                {t("Organization")}
                <FontAwesomeIcon
                  icon={getSortIcon("name")}
                  className="sort-icon"
                />
              </th>
              <th
                className="sortable"
                onClick={() => handleSort("primary_contact_email")}
              >
                {t("Primary Contact")}
                <FontAwesomeIcon
                  icon={getSortIcon("primary_contact_email")}
                  className="sort-icon"
                />
              </th>
              <th
                className="sortable"
                onClick={() => handleSort("admin_count")}
              >
                {t("Admins")}
                <FontAwesomeIcon
                  icon={getSortIcon("admin_count")}
                  className="sort-icon"
                />
              </th>
              <th
                className="sortable th-with-tooltip"
                onClick={() => handleSort("active_accounts")}
              >
                {t("Active")}
                <span
                  className="th-tooltip"
                  title={t("Company Admins + Company Users currently active")}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
                <FontAwesomeIcon
                  icon={getSortIcon("active_accounts")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("created_at")}>
                {t("Joined")}
                <FontAwesomeIcon
                  icon={getSortIcon("created_at")}
                  className="sort-icon"
                />
              </th>
              <th className="sortable" onClick={() => handleSort("is_active")}>
                {t("Status")}
                <FontAwesomeIcon
                  icon={getSortIcon("is_active")}
                  className="sort-icon"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredOrgs.map((org) => {
              const isSelected = selectedIds.includes(org.id);
              const isEditing = editingOrg?.id === org.id;
              const isEmailCopied = copiedEmailId === `org-email-${org.id}`;

              return (
                <tr
                  key={org.id}
                  className={`${!org.is_active ? "row-inactive" : ""} ${isSelected ? "row-selected" : ""} ${isEditing ? "row-editing" : ""}`}
                  onClick={() => handleEdit(org)}
                  style={{ cursor: "pointer" }}
                >
                  <td
                    className="td-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(org.id)}
                    />
                  </td>
                  <td>
                    <span className="cell-primary">{org.name}</span>
                  </td>
                  <td>
                    <div className="cell-contact-with-copy">
                      <div className="cell-contact">
                        <div className="cell-email-with-copy">
                          <span className="cell-primary">
                            {org.primary_contact_email}
                          </span>
                          <div className="email-actions">
                            <button
                              className="address-icon-btn"
                              onClick={(e) =>
                                handleCopyEmail(
                                  e,
                                  `org-email-${org.id}`,
                                  org.primary_contact_email,
                                )
                              }
                            >
                              <span
                                className={`copied-tooltip ${isEmailCopied ? "show" : ""}`}
                              >
                                {t("Copied!")}
                              </span>
                              <FontAwesomeIcon
                                icon={isEmailCopied ? faCheck : faCopy}
                                className={`address-icon ${isEmailCopied ? "copied" : ""}`}
                              />
                            </button>
                          </div>
                        </div>
                        <span className="cell-secondary">
                          {org.primary_contact_phone}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="td-center">
                    <span className="cell-count">{org.admin_count}</span>
                  </td>
                  <td className="td-center">
                    <span className="cell-count">{org.active_accounts}</span>
                    <span className="cell-secondary-inline">
                      {" "}
                      / {org.used_accounts}
                    </span>
                  </td>
                  <td>
                    <FormattedDate
                      date={org.created_at}
                      className="cell-date"
                    />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {togglingId === org.id ||
                    bulkTogglingIds.includes(org.id) ? (
                      <span
                        className={`status-pill ${bulkTogglingAction === "activate" || (!bulkTogglingAction && !org.is_active) ? "activating" : "deactivating"}`}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin />
                        {bulkTogglingAction === "activate" ||
                        (!bulkTogglingAction && !org.is_active)
                          ? t("Activating...")
                          : t("Deactivating...")}
                      </span>
                    ) : (
                      <button
                        className={`status-pill ${org.is_active ? "active" : "inactive"}`}
                        onClick={() => toggleActive(org.id)}
                        disabled={!!togglingId || bulkTogglingIds.length > 0}
                      >
                        <FontAwesomeIcon
                          icon={org.is_active ? faToggleOn : faToggleOff}
                        />
                        {org.is_active ? t("Active") : t("Inactive")}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredOrgs.length === 0 && (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faSitemap} />
            <p>{t("No organizations found")}</p>
          </div>
        )}
      </div>

      {/* table footer */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")} {filteredOrgs.length} {t("of")} {organizations.length}
        </span>
      </div>

      {/* inline create panel */}
      {showCreatePanel && (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar site-admin">
                <FontAwesomeIcon icon={faSitemap} />
              </div>
              <div>
                <h3>{t("New Organization")}</h3>
                <span>{t("Add a new organization to the platform")}</span>
              </div>
            </div>
            <button
              className="admin-detail-close"
              onClick={() => setShowCreatePanel(false)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="admin-detail-body">
            <div className="admin-detail-grid">
              <div className="admin-detail-field">
                <label>{t("Organization Name")} *</label>
                <input
                  type="text"
                  className="profile-input"
                  value={newOrg.name}
                  onChange={(e) =>
                    setNewOrg({ ...newOrg, name: e.target.value })
                  }
                  placeholder={t("Organization name")}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Max Accounts")}</label>
                <input
                  type="number"
                  className="profile-input"
                  value={newOrg.max_accounts}
                  onChange={(e) =>
                    setNewOrg({
                      ...newOrg,
                      max_accounts: parseInt(e.target.value) || 0,
                    })
                  }
                  min="1"
                  max="100"
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Primary Contact Email")}</label>
                <input
                  type="email"
                  className="profile-input"
                  value={newOrg.primary_contact_email}
                  onChange={(e) =>
                    setNewOrg({
                      ...newOrg,
                      primary_contact_email: e.target.value,
                    })
                  }
                  placeholder={t("Contact email")}
                />
              </div>
              <div className="admin-detail-field">
                <label>{t("Primary Contact Phone")}</label>
                <PhoneInput
                  value={newOrg.primary_contact_phone}
                  onChange={(val) =>
                    setNewOrg({ ...newOrg, primary_contact_phone: val })
                  }
                />
              </div>
            </div>
          </div>

          <div className="admin-detail-footer">
            <ActionButton
              onClick={() => setShowCreatePanel(false)}
              variant="default"
            >
              {t("Cancel")}
            </ActionButton>
            <ActionButton
              onClick={handleCreate}
              variant="secondary"
              disabled={!newOrg.name.trim()}
            >
              {t("Create")}
            </ActionButton>
          </div>
        </div>
      )}

      {/* inline edit panel */}
      {editingOrg && !showCreatePanel && (
        <div
          className="admin-detail-panel"
          ref={detailPanelRef}
          style={{ scrollMarginTop: "100px" }}
        >
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar site-admin">
                <FontAwesomeIcon icon={faSitemap} />
              </div>
              <div>
                <h3>{editingOrg.name}</h3>
                <span>
                  {editingOrg.used_accounts} / {editingOrg.max_accounts}{" "}
                  {t("accounts")}
                </span>
              </div>
            </div>
            <button
              className="admin-detail-close"
              onClick={() => {
                setEditingOrg(null);
                setOriginalEditingOrg(null);
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="admin-detail-body">
            {/* Organization Details Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faBuilding} />
                {t("Organization Details")}
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Organization Name")}</label>
                  <input
                    type="text"
                    className="profile-input"
                    value={editingOrg.name}
                    onChange={(e) =>
                      setEditingOrg({ ...editingOrg, name: e.target.value })
                    }
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Max Accounts")}</label>
                  <input
                    type="number"
                    className="profile-input"
                    value={editingOrg.max_accounts}
                    onChange={(e) =>
                      setEditingOrg({
                        ...editingOrg,
                        max_accounts: parseInt(e.target.value) || 0,
                      })
                    }
                    min={editingOrg.used_accounts}
                    max="100"
                  />
                  <small className="admin-input-hint">
                    {t("Cannot be less than current usage")} (
                    {editingOrg.used_accounts})
                  </small>
                </div>
                <div className="admin-detail-field">
                  <label>{t("Status")}</label>
                  <div className="toggle-switch-wrapper">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={editingOrg.is_active}
                        onChange={() =>
                          setEditingOrg({
                            ...editingOrg,
                            is_active: !editingOrg.is_active,
                          })
                        }
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span
                      className={`toggle-label ${editingOrg.is_active ? "active" : "inactive"}`}
                    >
                      {editingOrg.is_active ? t("Active") : t("Inactive")}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Contact Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faAddressBook} />
                {t("Primary Contact")}
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Email")}</label>
                  <input
                    type="email"
                    className="profile-input"
                    value={editingOrg.primary_contact_email || ""}
                    onChange={(e) =>
                      setEditingOrg({
                        ...editingOrg,
                        primary_contact_email: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Phone")}</label>
                  <PhoneInput
                    value={editingOrg.primary_contact_phone || ""}
                    onChange={(val) =>
                      setEditingOrg({
                        ...editingOrg,
                        primary_contact_phone: val,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="admin-detail-footer">
            <ActionButton
              onClick={() => {
                setEditingOrg(null);
                setOriginalEditingOrg(null);
              }}
              variant="default"
            >
              {t("Cancel")}
            </ActionButton>
            {hasFormChanges(originalEditingOrg, editingOrg) && (
              <ActionButton onClick={handleSaveEdit} variant="secondary">
                {t("Save Changes")}
              </ActionButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationsSection;
